import { useEffect, useMemo, useRef, useState } from 'react';
import {
  WORKBOARD_SPREADSHEET_COLUMN_OVERSCAN_COUNT,
  WORKBOARD_SPREADSHEET_DEFAULT_COLUMN_WIDTH,
  WORKBOARD_SPREADSHEET_ROW_OVERSCAN_COUNT,
  WORKBOARD_SPREADSHEET_VIRTUAL_ROW_HEIGHT,
} from 'dashboard409a/common/constants/workboardSpreadsheet';

type UseWorkboardSpreadsheetLayoutParams = {
  visibleColumnIds: string[];
  columnWidths?: Record<string, number>;
};

const useWorkboardSpreadsheetLayout = ({
  visibleColumnIds,
  columnWidths,
}: UseWorkboardSpreadsheetLayoutParams) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [viewportSize, setViewportSize] = useState({ height: 0, width: 0 });

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const updateSize = () => {
      setViewportSize({
        height: Math.floor(element.clientHeight),
        width: Math.floor(element.clientWidth),
      });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const columnWidthsForSpreadsheet = useMemo(() => {
    if (!columnWidths || Object.keys(columnWidths).length === 0) {
      return undefined;
    }
    // Keys must match columnTypes (column ids) so resize/reorder lookup works.
    return visibleColumnIds.reduce<{ column: string; width: number }[]>((acc, id) => {
      const width = columnWidths[id];
      if (width !== undefined) {
        acc.push({ column: id, width });
      }
      return acc;
    }, []);
  }, [columnWidths, visibleColumnIds]);

  const virtualColumnWidths = useMemo(
    () =>
      visibleColumnIds.map(id => columnWidths?.[id] ?? WORKBOARD_SPREADSHEET_DEFAULT_COLUMN_WIDTH),
    [columnWidths, visibleColumnIds]
  );

  const virtualization = useMemo(() => {
    if (viewportSize.height <= 0 || viewportSize.width <= 0 || virtualColumnWidths.length === 0) {
      return undefined;
    }
    return {
      rowHeight: WORKBOARD_SPREADSHEET_VIRTUAL_ROW_HEIGHT,
      rowOverscanCount: WORKBOARD_SPREADSHEET_ROW_OVERSCAN_COUNT,
      height: viewportSize.height,
      width: viewportSize.width,
      columnWidths: virtualColumnWidths,
      columnOverscanCount: WORKBOARD_SPREADSHEET_COLUMN_OVERSCAN_COUNT,
      // Header row + first visible column (Company Name by default); indicators are off.
      pinnedRowCount: 2,
      pinnedColumnCount: 1,
    };
  }, [viewportSize.height, viewportSize.width, virtualColumnWidths]);

  const isViewportReady = viewportSize.height > 0 && viewportSize.width > 0;

  return {
    containerRef,
    columnWidthsForSpreadsheet,
    virtualization,
    isViewportReady,
  };
};

export default useWorkboardSpreadsheetLayout;
